import { createModel } from "xstate/lib/model";
import {
  IFtthOrchestrator,
  IModemReturnOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import {
  fetchUnpaidInvoices,
  IInvoice,
  retryPayment,
} from "@nju/data-access-portal-payment";
import { IServiceStatus } from "@nju/data-access-portal-product";
import { changeSubscriptionStatus } from "@nju/data-access-portal-service";
import { BasicError, isOk } from "@nju/result";

interface IContext {
  productId?: string;
  payerId?: string;
  currentStatus?: IServiceStatus["ftthServiceStatus"];
  invoices: IInvoice[];
  modemReturnOrchestrator?: IModemReturnOrchestrator;
  hasNoProducts: boolean;
  ftthOrchestratorState?: IFtthOrchestrator["state"];
}

type IEvent =
  | { type: "SUBSCRIBE"; currentStatus: IServiceStatus["ftthServiceStatus"] }
  | { type: "SUBSCRIBE_SUCCESS" }
  | { type: "RETRY_PAYMENT" }
  | { type: "RETRY_PAYMENT_ERROR" }
  | { type: "RETRY_PAYMENT_SUCCESS" }
  | { type: "SUBSCRIBE_EXPIRED" }
  | { type: "SUBSCRIBE_ERROR" }
  | { type: "INVOICES_DONE"; invoices: IInvoice[] }
  | { type: "INVOICES_ERROR" };

const model = createModel<IContext, IEvent>({
  hasNoProducts: false,
  invoices: [],
});

const setCurrentStatus = model.assign(({ currentStatus }) => {
  return {
    currentStatus,
  };
}, "SUBSCRIBE");

const setInvoices = model.assign((_, { invoices }) => {
  if (invoices.length > 0) {
    return {
      invoices,
    };
  }
  return { invoices };
}, "INVOICES_DONE");

export const ftthServiceCTABannerMachine = model.createMachine(
  {
    initial: "invoices",
    states: {
      invoices: {
        initial: "ready",
        states: {
          ready: {
            always: [
              {
                target: "fetch",
                cond: "shouldFetchInvoices",
              },
              { target: "#idle" },
            ],
          },
          fetch: {
            invoke: {
              src: "fetchUnpaidInvoices",
            },
            on: {
              INVOICES_DONE: { actions: setInvoices, target: "#idle" },
              INVOICES_ERROR: "error",
            },
          },
          error: { type: "final" },
        },
      },
      idle: {
        id: "idle",
        on: {
          SUBSCRIBE: {
            actions: setCurrentStatus,
            target: "subscribe",
          },
          RETRY_PAYMENT: {
            target: "retryPayment",
          },
        },
      },
      retryPayment: {
        invoke: {
          src: "retryPayment",
        },
        on: {
          RETRY_PAYMENT_SUCCESS: {
            actions: "onRetryPaymentSuccess",
            type: "final",
          },
          RETRY_PAYMENT_ERROR: "error",
        },
      },
      subscribe: {
        invoke: {
          src: "changeSubscriptionStatus",
        },
        on: {
          SUBSCRIBE_SUCCESS: {
            actions: "onSubscribeSuccess",
            type: "final",
          },
          SUBSCRIBE_ERROR: "error",
          SUBSCRIBE_EXPIRED: "error",
        },
      },
      error: {
        type: "final",
      },
    },
  },
  {
    guards: {
      shouldFetchInvoices: ({
        hasNoProducts,
        modemReturnOrchestrator,
        ftthOrchestratorState,
      }) => {
        return (
          hasNoProducts &&
          (modemReturnOrchestrator?.state === "COMPLETED" ||
            modemReturnOrchestrator === undefined) &&
          (ftthOrchestratorState === "COMPLETED" ||
            ftthOrchestratorState === undefined)
        );
      },
    },
    services: {
      retryPayment: ({ payerId }) => {
        return async (callback) => {
          if (!payerId) {
            throw new BasicError({ message: "Missing payerId" });
          }

          const result = await retryPayment({ payerId });

          if (isOk(result)) {
            callback({ type: "RETRY_PAYMENT_SUCCESS" });
          } else {
            callback({ type: "RETRY_PAYMENT_ERROR" });
          }
        };
      },
      changeSubscriptionStatus: ({ productId, currentStatus }) => {
        return async (callback) => {
          if (!productId || !currentStatus) {
            throw new BasicError({ message: "Missing required fields" });
          }

          const result = await changeSubscriptionStatus(productId, {
            oldStatus: currentStatus,
            newStatus: "Active",
          });

          if (isOk(result)) {
            callback({ type: "SUBSCRIBE_SUCCESS" });
          } else {
            if (result.err.code === "UNSUBSCRIBE_TIME_EXPIRED") {
              callback({ type: "SUBSCRIBE_EXPIRED" });
            } else {
              callback({ type: "SUBSCRIBE_ERROR" });
            }
          }
        };
      },
      fetchUnpaidInvoices: () => {
        return async (callback) => {
          const result = await fetchUnpaidInvoices();

          if (isOk(result)) {
            return callback({
              type: "INVOICES_DONE",
              invoices: result.value.invoice,
            });
          }

          return callback({ type: "INVOICES_ERROR" });
        };
      },
    },
  },
);
